import React, { useState, useEffect } from 'react';
import { X, Download, Loader, CreditCard, Calendar, DollarSign, FileText, Droplets } from 'lucide-react';
import { formatCurrency, formatDate } from '../../utils/helpers';

const InvoiceModal = ({ isOpen, onClose, invoice }) => {
  const [loading, setLoading] = useState(false);

  if (!isOpen || !invoice) return null;

  const handleDownloadPDF = () => {
    if (invoice.pdfUrl) {
      window.open(invoice.pdfUrl, '_blank');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid: { color: 'bg-green-100 text-green-800 border-green-200', label: 'Paid' },
      open: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Pending' },
      void: { color: 'bg-gray-100 text-gray-800 border-gray-200', label: 'Void' },
      uncollectible: { color: 'bg-red-100 text-red-800 border-red-200', label: 'Failed' }
    };
    
    return badges[status] || badges.open;
  };

  const statusBadge = getStatusBadge(invoice.status);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between rounded-t-xl sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <Droplets className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Invoice</h2>
              <p className="text-blue-100 text-sm">
                {invoice.invoiceNumber || `#${invoice.invoiceId.substring(0, 8)}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {invoice.pdfUrl && (
              <button
                onClick={handleDownloadPDF}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
                title="Download PDF"
              >
                <Download size={20} />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Invoice Content */}
        <div className="p-6 space-y-6">
          
          {/* Status and Date Section */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-gray-200">
            <div>
              <p className="text-sm text-gray-600 mb-1">Status</p>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusBadge.color}`}>
                {statusBadge.label}
              </span>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-sm text-gray-600 mb-1">Invoice Date</p>
              <div className="flex items-center gap-2 text-gray-900">
                <Calendar size={16} className="text-gray-400" />
                <span className="font-medium">{formatDate(invoice.date)}</span>
              </div>
            </div>
          </div>

          {/* Company Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pb-4 border-b border-gray-200">
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                From
              </h3>
              <div className="text-gray-900">
                <p className="font-semibold text-lg">SuddyFlow</p>
                <p className="text-sm text-gray-600 mt-1">Pressure Washing CRM</p>
                <p className="text-sm text-gray-600">support@suddyflow.com</p>
              </div>
            </div>
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Bill To
              </h3>
              <div className="text-gray-900">
                <p className="font-semibold">Your Business</p>
                <p className="text-sm text-gray-600 mt-1">Account ID: {invoice.invoiceId.substring(0, 8)}</p>
              </div>
            </div>
          </div>

          {/* Invoice Details */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Invoice Details
            </h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-gray-700">
                  <FileText size={18} className="text-gray-400" />
                  <span className="font-medium">{invoice.description}</span>
                </div>
                <span className="text-gray-900 font-semibold">
                  {formatCurrency(invoice.amount)}
                </span>
              </div>
              
              {/* Subtotal Line */}
              <div className="border-t border-gray-200 pt-3 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-900">{formatCurrency(invoice.amount)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Tax</span>
                  <span className="text-gray-900">$0.00</span>
                </div>
              </div>

              {/* Total Line */}
              <div className="border-t-2 border-gray-300 mt-3 pt-3">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-gray-900">Total</span>
                  <span className="text-2xl font-bold text-blue-600">
                    {formatCurrency(invoice.amount)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          {invoice.status === 'paid' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CreditCard className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-green-900 mb-1">
                    Payment Received
                  </h3>
                  <p className="text-sm text-green-800">
                    This invoice has been paid in full on {formatDate(invoice.date)}.
                  </p>
                </div>
              </div>
            </div>
          )}

          {invoice.status === 'open' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-yellow-900 mb-1">
                    Payment Pending
                  </h3>
                  <p className="text-sm text-yellow-800">
                    This invoice is awaiting payment.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Footer Note */}
          <div className="text-center pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              Thank you for your business! Questions? Contact us at support@suddyflow.com
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-gray-50 px-6 py-4 flex gap-3 rounded-b-xl border-t border-gray-200">
          {invoice.pdfUrl && (
            <button
              onClick={handleDownloadPDF}
              className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <Download size={18} />
              Download PDF
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;